<template>
<ItpWidgetCard>
  <ItpBox
    alignItems="center"
    slot="header"
  >
    <strong>
      {{ 'Rental Services'|pgettext('RentalServices') }}
    </strong>
    <ItpButton
      :text="'Add rental service...'|pgettext('RentalServices')"
      variant="light"
      size="sm"
      class="ml-auto"
      @click.prevent.stop="evh_3343864469950044_clickPreventStop($event, {})"
    >
    </ItpButton>
  </ItpBox>
  <BListGroup
    class="rental__services"
    v-if="loaded"
  >
    <BListGroupItem
      v-if="!items.length"
    >
      <span>
        {{ 'No services.'|pgettext('RentalServices') }}
      </span>
    </BListGroupItem>
    <BListGroupItem
      :key="item.id"
      class="d-flex flex-row align-items-start"
      v-for="item in sortItems(items)"
    >
      <UcInactive
        :value="item.inactive"
        class="mr-2"
      >
      </UcInactive>
      <ItpBox
        direction="column"
      >
        <ItpBox
          alignItems="center"
          wrap-items
          class="mb-2"
        >
          <ItpLink
            :to="{
    name: 'app.rental',
    params: {id: item.rental.id}
  }"
            class="mr-2"
            v-if="rental == null"
          >
            <ItpText
              strong
              :text="item.rental.name"
            >
            </ItpText>
          </ItpLink>
          <UcCompactMeterDisplay
            :payload="item.meter"
            class="mr-2"
            v-if="meter == null"
          >
          </UcCompactMeterDisplay>
          <ItpText
            :strikethrough="isServiceStale(item)"
            class="mr-2 font-weight-bold"
          >
            {{ item.name }} ({{ item.billing_unit }})
          </ItpText>
          <ItpLink
            :icon="$config.externalLinkIcon"
            :to="{ name: 'app.product', params: { id: item.service.id } }"
            class="mr-2"
          >
          </ItpLink>
          <UcValidityPeriod
            :from="item.valid_from"
            :to="item.valid_to"
            class="mr-2"
            v-if="item.valid_from || item.valid_to"
          >
          </UcValidityPeriod>
        </ItpBox>
        <ItpBox
          alignItems="center"
          wrap-items
        >
          <UcBillingMultiplierBadge
            :value="item.meter_factor"
            class="mr-2"
            v-if="item.meter_factor != null && item.meter_factor != 1"
          >
          </UcBillingMultiplierBadge>
          <UcTariffCodeBadge
            :value="item.tariff_code"
            class="mr-2"
            v-if="item.tariff_code"
          >
          </UcTariffCodeBadge>
          <ItpBadge
            variant="warning"
            :text="'Non billable'|pgettext('RentalService')"
            no-box-shadow
            class="mr-2 border"
            v-if="item.non_billable"
          >
          </ItpBadge>
          <ItpBadge
            icon="mingcute:arrows-right-fill"
            :text="'Intermediated service'|pgettext('RentalService')"
            no-box-shadow
            class="mr-2 border"
            v-if="item.intermediated_service"
          >
          </ItpBadge>
        </ItpBox>
        <ItpBox
          class="mb-2"
          v-if="item.invoice_notes"
        >
          <DIV
            class="mx-1"
          >
            <ItpIcon
              icon="uil:notes"
            >
            </ItpIcon>
          </DIV>
          <ItpText
            spacing="px-1"
            :text="item.invoice_notes"
            pre
          >
          </ItpText>
        </ItpBox>
        <ItpBox>
          <ItpText
            :text="item.notes"
            pre
            v-if="item.notes"
          >
          </ItpText>
        </ItpBox>
        <UcRentalServiceCostAllocators
          :service="item"
          v-if="item.cost_allocators && item.cost_allocators.data && item.cost_allocators.data.length"
        >
        </UcRentalServiceCostAllocators>
      </ItpBox>
      <UcDropdownMenu>
        <b-dropdown-item-button
          @click.prevent.stop="evh_3475726502813997_clickPreventStop($event, {item})"
        >
          <ItpText
            :text="'Edit'|pgettext('Command')"
          >
          </ItpText>
        </b-dropdown-item-button>
        <b-dropdown-item-button
          @click.prevent.stop="evh_7286245325540822_clickPreventStop($event, {item})"
        >
          <ItpText
            :text="'Add cost allocator'|pgettext('RentalServiceCostAllocators')"
          >
          </ItpText>
        </b-dropdown-item-button>
      </UcDropdownMenu>
    </BListGroupItem>
  </BListGroup>
</ItpWidgetCard>
</template>

<script lang="ts">

import * as _ from 'lodash';
import Big from 'big.js';
import { Vue, Component, Watch, Prop } from 'vue-property-decorator';
import { mixins } from 'vue-class-component';
import {
  addRoute, UserComponentMixin, ComponentValueCache, actions
} from '@integro/core';

@Component()
export default class UcRentalServices extends mixins(UserComponentMixin) {
  componentMetadata = {
    kind: "usercontrol",
  };

  beforeCreate() {
  }

  sortItems(...args: any[]) {
    return _.sortBy(args[0], p => (this.isServiceStale(p) ? "_" : "" + p.id))
  }

  isServiceStale(...args: any[]) {
    const [service] = args;
    return service.inactive ||
      (service.valid_to && this.$fn.parseISODate(service.valid_to) < this.today)
  }

  @Prop({
    type: Object,
  })
  rental!: object;

  @Watch('rental')
  onRental(newValue: any, oldValue: any) {
    if (_.isEqual(newValue, oldValue)) return;
    this.loadDataThrottled("rental")
  }

  @Prop({
    type: Object,
  })
  meter!: object;

  @Watch('meter')
  onMeter(newValue: any, oldValue: any) {
    if (_.isEqual(newValue, oldValue)) return;
    this.loadDataThrottled("meter")
  }

  items!: any;

  async $$load_items() {
    return this.rental
      ? this.$fn.fetch('list_rental_service', { parameters: { rental_id: this.rental.id } })
      : this.$fn.fetch('list_meter_rental_services', { parameters: { meter_id: this.meter.id } })
  }

  ux!: any;
  dataMembers = ['items', 'ux'];

  data() {
    return {
      ...this.mixinData(),
      actions: this.getActions(),
      ...{
        items: null,
        ux: null,
      },
    }
  }

  get assignToRental() {
    if (this.loaded) {

      return this.meter != null
    }
    return null;
  }

  get preventEditMeter() {
    if (this.loaded) {

      return this.meter != null
    }
    return null;
  }

  get preventEditRental() {
    if (this.loaded) {

      return this.rental != null
    }
    return null;
  }

  async act_3343864469950044_emit_9804_getActionArgs($event: actions.ActionEvent): Promise<actions.EmitActionArgs> {
    // parameterResolver name=act_3343864469950044_emit_9804, alias=undefined
    return {
      event: "reload",
    }
  }

  async evh_3343864469950044_close_9803_getWhen($event: actions.ActionEvent): Promise<actions.EventHandlerImplArgs> {
    // parameterResolver name=evh_3343864469950044_close_9803, alias=undefined
    return !!$event.data.modalResult.value
  }

  async act_3343864469950044_showModal_9802_getActionArgs($event: actions.ActionEvent): Promise<actions.ShowModalActionArgs> {
    // parameterResolver name=act_3343864469950044_showModal_9802, alias=undefined
    return {
      name: "RentalService",
      props: {
        rental: this.rental,
        meter: this.meter,
        assignToRental: this.assignToRental,
        preventEditMeter: this.preventEditMeter,
      }
      ,
    }
  }

  async evh_3343864469950044_clickPreventStop(event: Event, scope: Dictionary<any>) {
    return await this.actions.evh_3343864469950044_clickPreventStop.executeFromDOM(this, event, scope);
  }

  async act_3475726502813997_emit_9808_getActionArgs($event: actions.ActionEvent): Promise<actions.EmitActionArgs> {
    // parameterResolver name=act_3475726502813997_emit_9808, alias=emitReload
    return {
      event: "reload",
    }
  }

  async evh_3475726502813997_close_9807_getWhen($event: actions.ActionEvent): Promise<actions.EventHandlerImplArgs> {
    // parameterResolver name=evh_3475726502813997_close_9807, alias=undefined
    return !!$event.data.modalResult.ok
  }

  async act_3475726502813997_showModal_9806_getActionArgs($event: actions.ActionEvent): Promise<actions.ShowModalActionArgs> {
    // parameterResolver name=act_3475726502813997_showModal_9806, alias=undefined
    return {
      name: "RentalService",
      props: {
        oid: $event.scope.item.id,
        mode: "edit",
        meter: this.meter,
        rental: this.rental,
        assignToRental: this.assignToRental,
        preventEditMeter: this.preventEditMeter,
        preventEditRental: this.preventEditRental,
      }
      ,
    }
  }

  async evh_3475726502813997_clickPreventStop(event: Event, scope: Dictionary<any>) {
    return await this.actions.evh_3475726502813997_clickPreventStop.executeFromDOM(this, event, scope);
  }

  async act_7286245325540822_showModal_9810_getActionArgs($event: actions.ActionEvent): Promise<actions.ShowModalActionArgs> {
    // parameterResolver name=act_7286245325540822_showModal_9810, alias=undefined
    return {
      name: "RentalServiceCostAllocator",
      props: {
        service: $event.scope.item,
      }
      ,
    }
  }

  async evh_7286245325540822_clickPreventStop(event: Event, scope: Dictionary<any>) {
    return await this.actions.evh_7286245325540822_clickPreventStop.executeFromDOM(this, event, scope);
  }

  actions!: {
    act_3343864469950044_emit_9804: actions.EmitAction;
    evh_3343864469950044_close_9803: actions.EventHandlerImpl;
    act_3343864469950044_showModal_9802: actions.ShowModalAction;
    evh_3343864469950044_clickPreventStop_9801: actions.EventHandlerImpl;
    evh_3343864469950044_clickPreventStop: actions.EventHandlerGroup;
    act_3475726502813997_emit_9808: actions.EmitAction;
    evh_3475726502813997_close_9807: actions.EventHandlerImpl;
    act_3475726502813997_showModal_9806: actions.ShowModalAction;
    evh_3475726502813997_clickPreventStop_9805: actions.EventHandlerImpl;
    evh_3475726502813997_clickPreventStop: actions.EventHandlerGroup;
    act_7286245325540822_showModal_9810: actions.ShowModalAction;
    evh_7286245325540822_clickPreventStop_9809: actions.EventHandlerImpl;
    evh_7286245325540822_clickPreventStop: actions.EventHandlerGroup;
  }

  getActions() {
    const act_3343864469950044_emit_9804 = new actions.EmitAction(
      {
        actionArgs: this.act_3343864469950044_emit_9804_getActionArgs,
        events: [],
      }
    );
    const evh_3343864469950044_close_9803 = new actions.EventHandlerImpl(
      {
        action: act_3343864469950044_emit_9804,
        event: "close",
        when: this.evh_3343864469950044_close_9803_getWhen,
        displayName: "emit",
      }
    );
    const act_3343864469950044_showModal_9802 = new actions.ShowModalAction(
      {
        actionArgs: this.act_3343864469950044_showModal_9802_getActionArgs,
        events: [evh_3343864469950044_close_9803],
      }
    );
    const evh_3343864469950044_clickPreventStop_9801 = new actions.EventHandlerImpl(
      {
        action: act_3343864469950044_showModal_9802,
        event: "click.prevent.stop",
        displayName: "showModal",
      }
    );
    const evh_3343864469950044_clickPreventStop = new actions.EventHandlerGroup(
      {
        handlers: [evh_3343864469950044_clickPreventStop_9801],
      }
    );
    const act_3475726502813997_emit_9808 = new actions.EmitAction(
      {
        actionArgs: this.act_3475726502813997_emit_9808_getActionArgs,
        displayName: "emitReload",
        events: [],
      }
    );
    const evh_3475726502813997_close_9807 = new actions.EventHandlerImpl(
      {
        action: act_3475726502813997_emit_9808,
        event: "close",
        when: this.evh_3475726502813997_close_9807_getWhen,
        displayName: "emitReload",
      }
    );
    const act_3475726502813997_showModal_9806 = new actions.ShowModalAction(
      {
        actionArgs: this.act_3475726502813997_showModal_9806_getActionArgs,
        events: [evh_3475726502813997_close_9807],
      }
    );
    const evh_3475726502813997_clickPreventStop_9805 = new actions.EventHandlerImpl(
      {
        action: act_3475726502813997_showModal_9806,
        event: "click.prevent.stop",
        displayName: "showModal",
      }
    );
    const evh_3475726502813997_clickPreventStop = new actions.EventHandlerGroup(
      {
        handlers: [evh_3475726502813997_clickPreventStop_9805],
      }
    );
    const act_7286245325540822_showModal_9810 = new actions.ShowModalAction(
      {
        actionArgs: this.act_7286245325540822_showModal_9810_getActionArgs,
        events: [],
      }
    );
    const evh_7286245325540822_clickPreventStop_9809 = new actions.EventHandlerImpl(
      {
        action: act_7286245325540822_showModal_9810,
        event: "click.prevent.stop",
        displayName: "showModal",
      }
    );
    const evh_7286245325540822_clickPreventStop = new actions.EventHandlerGroup(
      {
        handlers: [evh_7286245325540822_clickPreventStop_9809],
      }
    );
    return {
      act_3343864469950044_emit_9804,
      evh_3343864469950044_close_9803,
      act_3343864469950044_showModal_9802,
      evh_3343864469950044_clickPreventStop_9801,
      evh_3343864469950044_clickPreventStop,
      act_3475726502813997_emit_9808,
      evh_3475726502813997_close_9807,
      act_3475726502813997_showModal_9806,
      evh_3475726502813997_clickPreventStop_9805,
      evh_3475726502813997_clickPreventStop,
      act_7286245325540822_showModal_9810,
      evh_7286245325540822_clickPreventStop_9809,
      evh_7286245325540822_clickPreventStop,
      emitReload: act_3475726502813997_emit_9808,
    }
  }
}

Vue.component("UcRentalServices", UcRentalServices);

</script>